import { create } from 'zustand'
import { login as apiLogin, logout as apiLogout } from '../lib/api/client'
import type { AdminUser } from '../lib/api/types'

const LS_KEY = 'gsm_admin_user'

function loadSaved(): AdminUser | null {
  try {
    const raw = localStorage.getItem(LS_KEY)
    return raw ? (JSON.parse(raw) as AdminUser) : null
  } catch {
    return null
  }
}

interface AuthState {
  user: AdminUser | null
  status: 'loading' | 'authed' | 'anon'
  error: string | null
  restore: () => Promise<void>
  login: (u: string, p: string) => Promise<boolean>
  logout: () => Promise<void>
  /** Session died server-side (a 401) — clear locally and bounce to login. */
  expire: () => void
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'loading',
  error: null,
  // The Yep `currentuser` GET 301-redirects (a routing quirk), so we can't probe it.
  // The session cookie survives reloads, so we optimistically restore the saved user;
  // if the cookie is actually gone, API calls surface errors and the user re-logs in.
  restore: async () => {
    const saved = loadSaved()
    set({ user: saved, status: saved ? 'authed' : 'anon' })
  },
  login: async (username, password) => {
    set({ error: null })
    try {
      const user = await apiLogin(username, password)
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(user))
      } catch {
        /* ignore */
      }
      set({ user, status: 'authed' })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Login fallito' })
      return false
    }
  },
  logout: async () => {
    await apiLogout()
    try {
      localStorage.removeItem(LS_KEY)
    } catch {
      /* ignore */
    }
    set({ user: null, status: 'anon' })
  },
  expire: () =>
    set((s) => {
      if (s.status === 'anon') return s // already on the login screen
      try {
        localStorage.removeItem(LS_KEY)
      } catch {
        /* ignore */
      }
      return { user: null, status: 'anon', error: 'Sessione scaduta. Accedi di nuovo.' }
    }),
}))

// Any API call that returns 401 (expired Yep cookie) broadcasts this — drop to login.
if (typeof window !== 'undefined') {
  window.addEventListener('gsm:unauthorized', () => useAuth.getState().expire())
}
